import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileEdit, TestTube, Rocket, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const STATUS_CONFIG = {
  draft: {
    label: 'Draft',
    description: 'Portal is being configured. Not visible to anyone.',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    icon: FileEdit,
  },
  testing: {
    label: 'Testing in Pilot',
    description: 'Portal is visible to invited pilot testers only.',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    icon: TestTube,
  },
  live: {
    label: 'Live',
    description: 'Portal is fully public and visible to all users.',
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: Rocket,
  },
};

/**
 * PortalStatusSelector - Component to change portal status
 * Status Flow: DRAFT → TESTING IN PILOT → LIVE
 */
const PortalStatusSelector = ({ 
  portalId, 
  currentStatus = 'draft', 
  onStatusChange,
  compact = false,
  disabled = false 
}) => {
  const [status, setStatus] = useState(currentStatus);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const StatusIcon = config.icon;
  
  const handleStatusChange = (newStatus) => {
    // Going live requires confirmation
    if (newStatus === 'live' && status !== 'live') {
      setPendingStatus(newStatus);
      setShowConfirm(true);
    } else {
      confirmStatusChange(newStatus);
    }
  };
  
  const confirmStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/admin/portal-status/${portalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setStatus(newStatus);
        toast.success(`Portal status changed to ${STATUS_CONFIG[newStatus].label}`);
        onStatusChange?.(newStatus);
      } else {
        throw new Error(data.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    }
    setSaving(false);
    setShowConfirm(false);
    setPendingStatus(null);
  };
  
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Badge className={`${config.color} border`}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {config.label}
        </Badge>
        
        <Select
          value={status}
          onValueChange={handleStatusChange}
          disabled={disabled || saving}
        >
          <SelectTrigger className="w-[140px] h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-3 w-3" />
                    {cfg.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  return (
    <>
      <div className="space-y-3" data-testid="portal-status-selector">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${config.color} border px-3 py-1`}>
              <StatusIcon className="h-4 w-4 mr-1" />
              {config.label}
            </Badge>
          </div>
          
          <Select
            value={status}
            onValueChange={handleStatusChange}
            disabled={disabled || saving}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Change status..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span>{cfg.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        
        <p className="text-sm text-gray-500">{config.description}</p>
        
        {/* Status Flow Indicator */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className={status === 'draft' ? 'text-gray-700 font-medium' : ''}>Draft</span>
          <span>→</span>
          <span className={status === 'testing' ? 'text-amber-700 font-medium' : ''}>Testing</span>
          <span>→</span>
          <span className={status === 'live' ? 'text-green-700 font-medium' : ''}>Live</span>
        </div>
      </div>
      
      {/* Confirmation Dialog for Going Live */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Go Live
            </DialogTitle>
            <DialogDescription>
              Making this portal live will make it publicly accessible to all users. 
              Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-2">
            <p className="text-sm font-medium">Before going live, ensure:</p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4">
              <li>• All content and settings are configured correctly</li>
              <li>• Testing with pilot users has been completed</li>
              <li>• Welcome messages and branding are finalized</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => confirmStatusChange(pendingStatus)}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? 'Updating...' : 'Go Live'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PortalStatusSelector;
